import styles from "./StockOptionsChain.module.scss";
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useToggle } from "react-use";
import { Context } from "@/lib/Context";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { backgroundColorState, darkerBackgroundColorState, greyState, negativeState, newbackgroundColorState, positiveState, textColorState, themeState, userDataState } from "@/lib/store";
import { OptionsUiUserData, shouldLeaveLegacyOptions } from "src/hooks/useOptionsUiRedirect";
import { Stock } from "@/models/stock";
import { brighten } from "@/lib/ortex-charts/color";
import dayjs from "dayjs";
import type { XYChartOptions } from "@ortex-charts/react";
import { XYPlot } from "@/components/ortex-charts/Viz";
import Module from "@/components/module/Module";
import NoData from "@/components/no-data/NoData";
import StockOptionsRow from "@/components/stock-options-row/StockOptionsRow";
import RatioBar from "@/components/ratio-bar/RatioBar";
import { ChainData } from "@/models/stock-options";
import { classNames, permutation, statitics } from "@/lib/utils";
import { round } from "lodash";
import Checkbox from "../checkbox/Checkbox";

import Chip from "@mui/material/Chip";
import OutlinedBtn from "../outlined-btn/OutlinedBtn";
import Skeleton from "../skeleton/Skeleton";
import { CircularProgress, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PromoMessage from "@/components/tour/PromoMessage";
interface PropsTypes {
    stock: Stock;
    // Trading (Vertex) only: adds Calls/Puts action columns and opens an order ticket. When
    // absent the chain renders exactly as before.
    trading?: boolean;
    handleOpen?: (open: boolean, row: any, tradeAction: "Buy" | "Sell") => void;
}

const CHAIN_COLUMNS = ["Imp Volatility", "Open Int", "Volume", "Last Price", "Strikes", "Last Price", "Volume", "Open Int", "Imp Volatility"];
const TRADING_CHAIN_COLUMNS = ["Calls", ...CHAIN_COLUMNS, "Puts"];

const StockOptionsChain: FC<PropsTypes> = ({ stock, trading, handleOpen }) => {
    const { ortexWebSocket } = useContext(Context);
    const router = useRouter();
    const userData = useAtomValue(userDataState) as OptionsUiUserData | null | undefined;
    // Show the "try the new platform" promo only to users who are
    // actually STAYING on this legacy surface. Without the guard the
    // modal (a) flashed for users the redirect hook was about to move —
    // logging phantom impressions — and (b) popped on the NEW platform's
    // own Overview tab (which reuses this component), promoting the page
    // the user was already on.
    const showPlatformPromo = shouldLeaveLegacyOptions(userData) === false && !router.pathname.includes("options-platform");

    const theme = useAtomValue(themeState);
    const grey = useAtomValue(greyState);
    const positive = useAtomValue(positiveState);
    const negative = useAtomValue(negativeState);
    const textColor = useAtomValue(textColorState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const darkerBackgroundColor = useAtomValue(darkerBackgroundColorState);
    const highlightedBackgroundColor = brighten(darkerBackgroundColor, theme === "dark" ? 0.15 : -0.05);
    const newbackgroundColor = useAtomValue(newbackgroundColorState);

    const [isLoading, setIsLoading] = useState(true);

    const price = useRef<number>(undefined);
    const optionChainRef = useRef<HTMLDivElement>(null);
    const scrollData = useRef<{ strikes: number[]; median: number }>({ strikes: [], median: 0 });

    const dateSelectorRef = useRef<HTMLDivElement>(null);
    const [datesScrollEnd, setDatesScrollEnd] = useState(false);
    const [datesScrollStart, setDatesScrollStart] = useState(true);

    const handleScroll = () => {
        const scrollWidth = dateSelectorRef.current.scrollWidth;
        const width = dateSelectorRef.current.clientWidth;
        const scrollLeft = dateSelectorRef.current.scrollLeft;

        if (scrollLeft > 0) setDatesScrollStart(false);
        else setDatesScrollStart(true);

        if (scrollWidth - width <= scrollLeft) setDatesScrollEnd(true);
        else setDatesScrollEnd(false);
    };

    const scrollTo = (side: "left" | "right") => {
        dateSelectorRef.current.scroll({
            left: side === "left" ? 0 : dateSelectorRef.current.scrollWidth,
            top: 0,
            behavior: "smooth",
        });
    };

    const [isChart, toggleChart] = useToggle(false);
    const [breakEvenPrices, toggleBreakEvenPrices] = useToggle(false);

    const [currentPrice, setCurrentPrice] = useState<number>();
    const [trades, setTrades] = useState<[{ o: number; h: number; l: number; c: number }, number][]>();
    const [optionChain, setOptionChain] = useState<[string, { C: ChainData; P: ChainData }][]>([]);
    const [expiryDate, setExpiryDate] = useState<string>();
    const [chainLoading, setChainLoading] = useState(true);
    const [dates, setDates] = useState<string[]>([]);
    const [ratios, setRatios] = useState<number[]>([]);
    const [noData, setNoData] = useState(false);
    const [chainPoints, setChainPoints] = useState<{ calls: Array<{ x: number; y: number; size: number }>; puts: Array<{ x: number; y: number; size: number }> }>({ calls: [], puts: [] });
    const identifier = `${stock.ticker}${Array(6 - stock.ticker.length)
        .fill("_")
        .join("")}${expiryDate}`;

    const sendPriceMessage = useCallback(() => {
        ortexWebSocket.send(JSON.stringify({ type: "last_trades", identifier: stock.ticker }));
    }, [ortexWebSocket]);

    const priceMessage = useCallback(
        ({ data }: MessageEvent) => {
            const { LT, T } = JSON.parse(data);
            if (T) setCurrentPrice(T.C.c);
            else if (LT) {
                setCurrentPrice(LT[LT.length - 1]?.[0].c);
                setTrades(LT.reverse());
            }
        },
        [ortexWebSocket]
    );

    const sendExpiriesMessage = useCallback(() => {
        ortexWebSocket.send(JSON.stringify({ type: "optionexpiries", identifier: stock.ticker }));
    }, [ortexWebSocket]);

    const expiriesMessage = useCallback(
        ({ data }: MessageEvent) => {
            const { msg, optionexpiries, status } = JSON.parse(data);
            if (optionexpiries && stock.ticker in optionexpiries) {
                if (Array.isArray(optionexpiries[stock.ticker]) && optionexpiries[stock.ticker].length) {
                    (optionexpiries[stock.ticker] as string[]).sort((a, b) => {
                        const dateA = dayjs.utc(a, "YYMMDD");
                        const dateB = dayjs.utc(b, "YYMMDD");
                        return dateA.isAfter(dateB) ? 1 : dateA.isBefore(dateB) ? -1 : 0;
                    });
                    setDates(optionexpiries[stock.ticker]);
                    const today = dayjs.utc().startOf("day");
                    const nextAvailableDate = (optionexpiries[stock.ticker] as string[]).find((date) => {
                        const expiryDate = dayjs.utc(date, "YYMMDD");
                        return !expiryDate.isBefore(today, "day");
                    });
                    setExpiryDate(nextAvailableDate || optionexpiries[stock.ticker][0]);
                } else {
                    setNoData(true);
                    setChainLoading(false);
                }
            } else if (status === "error" && (msg as string).includes(stock.ticker)) {
                setNoData(true);
                setChainLoading(false);
            }
        },
        [ortexWebSocket]
    );

    const sendChainMessage = useCallback(() => {
        ortexWebSocket.send(JSON.stringify({ type: "optionchain", identifier }));
        ortexWebSocket.send(JSON.stringify({ type: "subscribe", identifier: identifier + "***" }));
    }, [ortexWebSocket, expiryDate]);

    const chainMessage = useCallback(
        ({ data }: MessageEvent) => {
            const { OC } = JSON.parse(data);
            if (OC?.[identifier]) {
                const data = Object.entries(OC[identifier]) as [string, { C: ChainData; P: ChainData }][];
                data.sort(([a], [b]) => Number(a) - Number(b));
                setOptionChain(data);
                setChainPoints({
                    calls: data.map((point) => ({ x: Number(point[0]), y: point[1].C.OI, size: point[1].C.V })),
                    puts: data.map((point) => ({ x: Number(point[0]), y: point[1].P.OI, size: point[1].P.V })),
                });
                setRatios(permutation(["C", "P"], ["OI", "V"]).map((key) => data.reduce((acc, [_, chainData]) => acc + (chainData[key[0]][key[1]] || 0), 0)));
            }
            setIsLoading(false);
        },
        [ortexWebSocket, expiryDate]
    );

    useEffect(() => {
        if (ortexWebSocket) {
            ortexWebSocket.readyState === 1 ? sendPriceMessage() : ortexWebSocket.addEventListener("open", sendPriceMessage);
            ortexWebSocket.addEventListener("message", priceMessage);
        }

        return () => {
            if (ortexWebSocket) {
                ortexWebSocket.removeEventListener("open", sendPriceMessage);
                ortexWebSocket.removeEventListener("message", priceMessage);
            }
        };
    }, [ortexWebSocket]);

    useEffect(() => {
        if (ortexWebSocket) {
            ortexWebSocket.readyState === 1 ? sendExpiriesMessage() : ortexWebSocket.addEventListener("open", sendExpiriesMessage);
            ortexWebSocket.addEventListener("message", expiriesMessage);
        }

        return () => {
            if (ortexWebSocket) {
                ortexWebSocket.removeEventListener("open", sendExpiriesMessage);
                ortexWebSocket.removeEventListener("message", expiriesMessage);
            }
        };
    }, [ortexWebSocket]);

    useEffect(() => {
        if (ortexWebSocket && expiryDate) {
            if (!isChart) setChainLoading(true);
            ortexWebSocket.readyState === 1 ? sendChainMessage() : ortexWebSocket.addEventListener("open", sendChainMessage);
            ortexWebSocket.addEventListener("message", chainMessage);
        }

        return () => {
            if (ortexWebSocket && expiryDate) {
                ortexWebSocket.removeEventListener("open", sendChainMessage);
                ortexWebSocket.removeEventListener("message", chainMessage);
                // A closed or replaced socket throws on send and has nothing left to unsubscribe.
                if (ortexWebSocket.readyState === WebSocket.OPEN) ortexWebSocket.send(JSON.stringify({ type: "unsubscribe", identifier: identifier + "***" }));
            }
        };
    }, [ortexWebSocket, expiryDate]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (optionChain.length) {
            if (!isChart) setChainLoading(false);

            const strikes = optionChain.map(([strike]) => Number(strike));
            const median = statitics(strikes)[2];
            scrollData.current = { strikes, median };

            timeout = setTimeout(() => {
                optionChainRef.current?.scroll(0, optionChainRef.current?.scrollHeight * (strikes.findIndex((strike) => strike >= (price.current ?? median)) / strikes.length) - optionChainRef.current?.clientHeight / 2);
            }, 1000);
        }

        return () => {
            if (optionChain.length) clearTimeout(timeout);
        };
    }, [optionChain]);

    useEffect(() => {
        if (!isChart)
            optionChainRef.current?.scroll(
                0,
                optionChainRef.current?.scrollHeight * (scrollData.current.strikes.findIndex((strike) => strike >= (price.current ?? scrollData.current.median)) / scrollData.current.strikes.length) - optionChainRef.current?.clientHeight / 2
            );
    }, [isChart]);

    useEffect(() => {
        if (!isFinite(price.current) && isFinite(currentPrice)) price.current = currentPrice;
    }, [currentPrice]);

    useEffect(() => {
        if (!isChart)
            optionChainRef.current?.scroll(
                0,
                optionChainRef.current?.scrollHeight * (scrollData.current.strikes.findIndex((strike) => strike >= (price.current ?? scrollData.current.median)) / scrollData.current.strikes.length) - optionChainRef.current?.clientHeight / 2
            );
    }, [isChart]);

    useEffect(() => {
        if (!isFinite(price.current) && isFinite(currentPrice)) price.current = currentPrice;
    }, [currentPrice]);

    const chartOptions = useMemo<Partial<XYChartOptions>>(
        () => ({
            series: [
                { key: "calls", title: "Call", kind: "bubble", data: chainPoints.calls, color: positive },
                { key: "puts", title: "Put", kind: "bubble", data: chainPoints.puts, color: negative },
            ],
            referenceLines: isFinite(currentPrice) ? [{ value: currentPrice, axis: "x", color: grey, lineStyle: "dashed", label: "Spot" }] : [],
            legend: true,
            xAxis: { visible: true, gridlines: true, format: { type: "price", precision: 2 }, title: "Strike Price" },
            yAxis: { visible: true, gridlines: true, format: { type: "compact", precision: 0 }, title: "Open Interest" },
            tooltipTitle: (x: number) => `Strike ${x}`,
        }),
        [chainPoints, positive, negative, grey, currentPrice]
    );

    return isLoading ? (
        <Skeleton variant="rounded" width="100%" height="100%" loading={isLoading} />
    ) : (
        <Module
            title="Option Chain"
            help={{ moduleId: "option_chain", modulePage: "V2_stock_options" }}
            allowFullscreen
            feedback
            settingsComponent={expiryDate && <OutlinedBtn onClick={toggleChart}>Show as {isChart ? "Table" : "Bubble Chart"}</OutlinedBtn>}
            noHeader={trading}
        >
            <style jsx>{`
                th {
                    background-color: ${backgroundColor};
                }
                table,
                th div {
                    border-color: ${darkerBackgroundColor};
                }
                @media (hover: hover) {
                    button:hover {
                        background-color: ${newbackgroundColor} !important;
                    }
                }
            `}</style>
            <div className={styles.StockOptionsChain}>
                {trading && <Typography sx={{ marginBottom: 1 }}>Option Expiries</Typography>}
                {showPlatformPromo && <PromoMessage tourId="options_platform_promo" ctaHrefOverride={`/options-platform/chain/${stock.ticker}`} />}
                {/* First-3-then-every-5th announcement modal. Context-aware:
                    "Try it" goes straight to this stock's platform page
                    rather than the generic ticker picker. */}
                <div className={styles["chain-date-selector"]}>
                    <IconButton onClick={() => scrollTo("left")} sx={{ backgroundColor: newbackgroundColor, ":hover": { backgroundColor: newbackgroundColor } }} className={classNames(styles.arrow, styles.left, styles[datesScrollStart && "hidden"])}>
                        <ChevronLeftIcon sx={{ color: textColor }} />
                    </IconButton>
                    <div className={classNames(!datesScrollEnd && styles.scrolleableLeft, !datesScrollStart && styles.scrolleableRight)} onScroll={handleScroll} ref={dateSelectorRef}>
                        <div>
                            {dates.map((date) => (
                                <Chip
                                    label={dayjs(date, "YYMMDD").format("DD MMM YY")}
                                    variant="outlined"
                                    sx={{ marginRight: 1 }}
                                    onClick={() => setExpiryDate(date)}
                                    style={{ color: textColor, backgroundColor: date === expiryDate && highlightedBackgroundColor }}
                                    key={date}
                                />
                            ))}
                        </div>
                    </div>
                    <IconButton onClick={() => scrollTo("right")} sx={{ backgroundColor: newbackgroundColor, ":hover": { backgroundColor: newbackgroundColor } }} className={classNames(styles.arrow, styles.right, styles[datesScrollEnd && "hidden"])}>
                        <ChevronRightIcon sx={{ color: textColor }} />
                    </IconButton>
                </div>
                <div className={styles["chain-ratios"]}>
                    <div className={styles.titles}>
                        <Typography variant="body2" className="positive">
                            Calls
                        </Typography>
                        {expiryDate && (
                            <div>
                                <Checkbox checked={breakEvenPrices} onChange={toggleBreakEvenPrices}>
                                    <Typography variant="body2">{breakEvenPrices ? "Hide" : "Show"} break even prices</Typography>
                                </Checkbox>
                            </div>
                        )}
                        <Typography variant="body2" className="negative">
                            Puts
                        </Typography>
                    </div>
                    <div className={styles.values}>
                        <div className={styles.a}>
                            <Typography variant="body2">Open Interest: {round((ratios[0] / (ratios[0] + ratios[2])) * 100, 2) || "N/A"}%</Typography>
                        </div>
                        <div className={styles.b}>
                            <Typography variant="body2">Volume: {round((ratios[1] / (ratios[1] + ratios[3])) * 100, 2) || "N/A"}%</Typography>
                        </div>
                        <div className={styles.c}>
                            <Typography variant="body2" className={styles.ratio}>
                                PCR (OI): {round(ratios[2] / ratios[0], 2) || "N/A"}
                            </Typography>
                            <RatioBar value={ratios[2] / ratios[0]} />
                        </div>
                        <div className={styles.d}>
                            <Typography variant="body2" className={styles.ratio}>
                                PCR (Vol): {round(ratios[3] / ratios[1], 2) || "N/A"}
                            </Typography>
                            <RatioBar value={ratios[3] / ratios[1]} />
                        </div>
                        <div className={styles.e}>
                            <Typography variant="body2">Volume: {round((ratios[3] / (ratios[1] + ratios[3])) * 100, 2) || "N/A"}%</Typography>
                        </div>
                        <div className={styles.g}>
                            <Typography variant="body2">Open Interest: {round((ratios[2] / (ratios[0] + ratios[2])) * 100, 2) || "N/A"}%</Typography>
                        </div>
                    </div>
                </div>
                {isChart ? (
                    <div className={styles.chainChart}>
                        <XYPlot options={chartOptions} />
                    </div>
                ) : (
                    <div className={styles["chain-table"]} style={trading ? { height: "75%" /* , flex: "flex-grow" (not a valid flex value; no effect) */ } : undefined} ref={optionChainRef}>
                        {chainLoading ? (
                            <div className={styles.loading}>
                                <CircularProgress />
                            </div>
                        ) : noData ? (
                            <NoData />
                        ) : (
                            <table>
                                <thead>
                                    <tr>
                                        {(trading ? TRADING_CHAIN_COLUMNS : CHAIN_COLUMNS).map((el, index) => {
                                            return (
                                                <th key={`${el} ${index}`}>
                                                    <div>
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {el}
                                                        </Typography>
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {trades &&
                                        optionChain.map((data) => {
                                            const callTimestamp = data[1].C.TS;
                                            const putTimestamp = data[1].P.TS;

                                            // Convert trade timestamps from milliseconds to seconds for comparison
                                            const callPrice = trades.find((item) => item[1] / 1000 <= callTimestamp)?.[0]?.c;
                                            const putPrice = trades.find((item) => item[1] / 1000 <= putTimestamp)?.[0]?.c;

                                            return (
                                                <StockOptionsRow
                                                    key={data[0]}
                                                    data={data}
                                                    expiryDate={expiryDate}
                                                    price={currentPrice}
                                                    callPrice={callPrice}
                                                    putPrice={putPrice}
                                                    breakEvenPrices={breakEvenPrices}
                                                    trading={trading}
                                                    handleOpen={handleOpen}
                                                />
                                            );
                                        })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </Module>
    );
};

export default StockOptionsChain;
